import { useEffect } from 'react';
import { datadogRum, RumEvent } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { LogError } from './loggingHelpers';
import { redactSensitiveParams } from './redactSensitiveParams';

// RUM records view.url on mount, before any page can tidy its own address bar —
// so a token in the query (ENG-3878) ships unless scrubbed here.
/* eslint-disable no-param-reassign -- Datadog's beforeSend contract is mutate-in-place */
const redactViewUrls = (event: { view?: { url?: string; referrer?: string } }): void => {
  if (event.view?.url) event.view.url = redactSensitiveParams(event.view.url);
  if (event.view?.referrer) event.view.referrer = redactSensitiveParams(event.view.referrer);
};

const redactRumEventUrls = (event: RumEvent): void => {
  redactViewUrls(event);
  if (event.type === 'resource' && event.resource?.url) {
    event.resource.url = redactSensitiveParams(event.resource.url);
  }
};
/* eslint-enable no-param-reassign */

export const useDatadogBrowser = () => {
  useEffect(() => {
    const applicationId = globalThis.process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID || '';
    const clientToken = globalThis.process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || '';
    const environment = globalThis.process.env.NEXT_PUBLIC_APP_ENV || 'development';
    const version = globalThis.process.env.NEXT_PUBLIC_VERSION || '0.0.0';

    if (applicationId && clientToken) {
      datadogRum.init({
        applicationId,
        clientToken,
        sessionSampleRate: 5,
        sessionReplaySampleRate: 0,
        traceSampleRate: 5,
        traceContextInjection: 'sampled',
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        service: 'engage-public-frontend',
        env: environment,
        version,
        beforeSend: (event) => {
          redactRumEventUrls(event);
          return true;
        },
      });
      datadogLogs.init({
        clientToken,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: ['warn', 'error', 'info'],
        sessionSampleRate: 5,
        telemetrySampleRate: 0,
        service: 'engage-public-frontend',
        env: environment,
        version,
        // forwardErrorsToLogs means an uncaught error ships the view URL as context.
        beforeSend: (log) => {
          redactViewUrls(log);
          return true;
        },
      });
    }
  }, []);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const { filename, lineno, colno } = event;
      const error = event.error as Error;
      const errorData = {
        type: error.constructor.name,
        message: error.message,
        stack: error.stack,
        timestamp: Date.now(),
        url: redactSensitiveParams(window.location.href),
        userAgent: navigator.userAgent,
        filename,
        lineno,
        colno,
      };
      LogError('Global Error', error, errorData);
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);
};
